import React from 'react';
import { Check, Copy, UserPlus } from 'lucide-react';
import { buildReferralUrl, maskEmail } from '../../utils/referral';
import { useCopyReferralLink } from '../../hooks/useCopyReferralLink';
import type { ReferralRecord } from '../../types/referral';
interface ReferralHistoryProps {
  referrals: ReferralRecord[];
  referralCode: string | null;
  loading?: boolean;
  error?: string | null;
}
function formatDate(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}
function StatusBadge({
  status
}: {
  status: ReferralRecord['status'];
}) {
  if (status === 'rewarded') {
    return <span className="inline-flex items-center gap-1.5 rounded-full border border-green-500/40 bg-green-500/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-green-400" data-id="element-1753" data-mp="n56yw6ed143i">
        <Check size={12} aria-hidden="true" data-id="element-1754" data-mp="s64hul772262" /> Rewarded
      </span>;
  }
  return <span className="inline-flex items-center rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-amber-400" data-id="element-1755" data-mp="3ngrj4qyvwufe">
      Pending
    </span>;
}
function RewardText({
  status
}: {
  status: ReferralRecord['status'];
}) {
  return status === 'rewarded' ? <span className="text-sm font-bold text-green-400" data-id="element-1756" data-mp="39lgp1fsgsdc3">Earned +5 credits</span> : <span className="text-sm text-gray-500" data-id="element-1757" data-mp="34m4799gg9wgd">Awaiting first scan</span>;
}
export function ReferralHistory({
  referrals,
  referralCode,
  loading = false,
  error = null
}: ReferralHistoryProps) {
  const {
    copied,
    copy
  } = useCopyReferralLink(buildReferralUrl(referralCode));
  return <section aria-labelledby="referral-history-heading" data-id="element-1758" data-mp="2quftz2ugt1xt">
      <h2 id="referral-history-heading" className="mb-5 flex items-center gap-2 text-lg font-black uppercase tracking-tight text-white" data-id="element-1759" data-mp="2ltrv5uzzvgt1">
        <span aria-hidden="true" className="h-4 w-1 bg-[#FF0000]" data-id="element-1760" data-mp="2gwropiz8gc1e" />
        Referral history
      </h2>

      {loading ? <div className="space-y-3" aria-live="polite" data-id="element-1761" data-mp="2c6fhqbosi6o6">
          {[0, 1, 2].map(row => <div key={row} className="h-16 animate-pulse rounded-2xl border border-gray-800 bg-[#0A0A0A]" data-id="element-1762" data-mp="1y6ejz53tnfcx" />)}
        </div> : error ? <p role="alert" className="rounded-2xl border border-[#FF0000]/30 bg-[#FF0000]/10 p-5 text-sm text-[#FF6B6B]" data-id="element-1763" data-mp="2pxkmfs70lp5d">
          {error}
        </p> : referrals.length === 0 ? <div className="rounded-2xl border border-gray-800 bg-[#0A0A0A] p-8 text-center md:p-12" data-id="element-1764" data-mp="2h5iktql7m2ti">
          <div className="mx-auto mb-5 inline-flex h-12 w-12 items-center justify-center rounded-xl border border-gray-700 bg-gray-900" data-id="element-1765" data-mp="2l62nf9ojxyn1">
            <UserPlus className="h-5 w-5 text-gray-500" aria-hidden="true" data-id="element-1766" data-mp="38ulvbo3ow9el" />
          </div>
          <h3 className="text-base font-black uppercase tracking-wide text-white" data-id="element-1767" data-mp="3dog96awm5u5r">
            No referrals yet
          </h3>
          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-gray-400" data-id="element-1768" data-mp="125tmwo5fpr5j">
            Share your ToxicDetector link with someone you know and earn free
            scan credits when they complete their first analysis.
          </p>
          <button type="button" onClick={copy} className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl bg-[#FF0000] px-6 py-3.5 text-sm font-black uppercase tracking-wider text-white transition-colors duration-200 ease-out hover:bg-[#D90000] focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#050505]" data-id="element-1769" data-mp="16ycvt7t6fy40">
            {copied ? <Check size={16} aria-hidden="true" data-id="element-1770" data-mp="o6k5xm9emt0i" /> : <Copy size={16} aria-hidden="true" data-id="element-1771" data-mp="24llopnvic9g1" />}
            {copied ? 'Copied ✓' : 'Copy referral link'}
          </button>
        </div> : <>
          {/* Desktop table */}
          <div className="hidden overflow-hidden rounded-2xl border border-gray-800 bg-[#0A0A0A] md:block" data-id="element-1772" data-mp="9785rmtn990h">
            <table className="w-full text-left" data-id="element-1773" data-mp="e62nsmjsetjj">
              <caption className="sr-only" data-id="element-1774" data-mp="i6uzrnk1ncji">
                Friends you invited, when they joined, and the reward status of
                each invite
              </caption>
              <thead data-id="element-1775" data-mp="n3v49u18w78i">
                <tr className="border-b border-gray-800" data-id="element-1776" data-mp="s52es0urvkiv">
                  {['Friend', 'Date', 'Status', 'Reward'].map(heading => <th key={heading} scope="col" className="px-6 py-4 font-mono text-[10px] uppercase tracking-[0.18em] text-gray-500" data-id="element-1777" data-mp="x33woiggpdsr">
                      {heading}
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-1778" data-mp="1atpee3u6jhp2">
                {referrals.map(referral => <tr key={referral.id} className="border-b border-gray-800/60 last:border-b-0" data-id="element-1779" data-mp="1fv4z1usg3m6x">
                    <td className="px-6 py-4 font-mono text-sm text-gray-200" data-id="element-1780" data-mp="1kyxr0bczt2kq">
                      {maskEmail(referral.email)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-400" data-id="element-1781" data-mp="1g0ntvv7pjrwh">
                      {formatDate(referral.createdAt)}
                    </td>
                    <td className="px-6 py-4" data-id="element-1782" data-mp="28pkywwmwrs7a">
                      <StatusBadge status={referral.status} data-id="element-1783" data-mp="28o9uculn2gf4" />
                    </td>
                    <td className="px-6 py-4" data-id="element-1784" data-mp="23qi74dvxvih9">
                      <RewardText status={referral.status} data-id="element-1785" data-mp="1yz03tj6g15t4" />
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <ul className="space-y-3 md:hidden" data-id="element-1786" data-mp="2rg2r903twls4">
            {referrals.map(referral => <li key={referral.id} className="rounded-2xl border border-gray-800 bg-[#0A0A0A] p-5" data-id="element-1787" data-mp="2mp2qfjrl9jkc">
                <div className="flex items-start justify-between gap-3" data-id="element-1788" data-mp="krsjra36l5xb">
                  <p className="min-w-0 truncate font-mono text-sm text-gray-200" data-id="element-1789" data-mp="ppc14iv6dyth">
                    {maskEmail(referral.email)}
                  </p>
                  <StatusBadge status={referral.status} data-id="element-1790" data-mp="azmsy3oo0ujp" />
                </div>
                <div className="mt-3 flex items-center justify-between gap-3" data-id="element-1791" data-mp="fyr0yqzusm8g">
                  <span className="text-xs text-gray-500" data-id="element-1792" data-mp="1zzplccdleo0">
                    {formatDate(referral.createdAt)}
                  </span>
                  <RewardText status={referral.status} data-id="element-1793" data-mp="6xj8wqnxkhmt" />
                </div>
              </li>)}
          </ul>
        </>}
    </section>;
}